import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://ballotr.com';
const PAGE_TITLE = 'Terms of Service | Ballotr';
const PAGE_DESCRIPTION =
  'Read Ballotr’s Terms of Service, including eligibility, platform conduct, sponsored content, third-party donation redirects, and user responsibilities.';
const PAGE_IMAGE = `${SITE_URL}/logo192.png`;

const sections = [
  {
    title: '1. About Ballotr',
    body: [
      'Ballotr is a civic engagement platform that allows users to discover political issues, express support or opposition to campaigns, comment on civic issues, and signal contribution intent.',
      'Ballotr may also generate aggregated civic insights derived from anonymized platform activity to better understand civic engagement trends.',
      'Ballotr does not endorse any political organization, candidate, or policy position presented on the platform.',
    ],
  },
  {
    title: '2. Eligibility',
    body: [
      'Ballotr is intended only for individuals who are 18 years of age or older and reside in the United States.',
      'By creating an account, you represent that you meet these requirements.',
    ],
  },
  {
    title: '3. User Accounts',
    body: [
      'To access certain features of Ballotr, you must create an account.',
      'You agree to provide accurate information, maintain the security of your login credentials, and be responsible for activity under your account.',
      'Ballotr may suspend or terminate accounts that violate these Terms.',
    ],
  },
  {
    title: '4. Platform Content and Campaigns',
    body: [
      'Campaigns displayed on Ballotr may be generated automatically from publicly available news sources and other information feeds.',
      'Ballotr does not guarantee the accuracy or completeness of third-party content.',
      'Campaigns presented on the platform do not represent endorsements by Ballotr.',
    ],
  },
  {
    title: '5. User Engagement',
    body: [
      'Users may interact with campaigns by supporting or opposing campaigns, commenting on campaigns, and signaling contribution intent.',
      "Comments may be visible to other users and may display the user's first name and last initial.",
      'Users are solely responsible for content they post.',
      'Ballotr does not verify off-platform donations.',
    ],
  },
  {
    title: '6. User-Generated Content Responsibility',
    body: [
      'Ballotr may allow users to post comments and other content.',
      'Users are solely responsible for the content they post.',
      'Ballotr does not endorse, verify, or guarantee the accuracy of user-generated content.',
      'User-generated content reflects the views of individual users and does not represent the views of Ballotr or Bay View Concepts LLC.',
      'To the fullest extent permitted by law, Ballotr shall not be liable for user-generated content posted on the platform.',
    ],
  },
  {
    title: '7. Content Moderation',
    body: [
      'Ballotr reserves the right, but not the obligation, to review, remove, or restrict access to any content that violates these Terms or the Ballotr Community Guidelines.',
      'Ballotr may suspend or terminate accounts that repeatedly violate platform policies.',
    ],
  },
  {
    title: '8. Prohibited Conduct',
    bullets: [
      'Post threats, harassment, or abusive content',
      'Impersonate others',
      'Use automated systems or bots to manipulate engagement',
      'Post spam or unrelated promotional content',
      'Attempt to manipulate voting or civic engagement metrics',
      'Engage in illegal activities through the platform',
    ],
  },
  {
    title: '9. Donations and Third-Party Services',
    body: [
      'Ballotr may allow users to signal support or opposition to campaigns and may redirect users to third-party websites where donations may be completed.',
      'Ballotr does not currently process political donations.',
      'All donations occur on third-party platforms and are subject to those platforms’ policies and terms.',
      'Ballotr is not responsible for third-party transactions or disputes.',
    ],
  },
  {
        title: '10. Government and Political Activity Disclaimer',
        body: [
          'Ballotr is an independent civic engagement platform designed to facilitate discussion and awareness of public policy issues.',
          'Ballotr is not a government app and is not affiliated with, endorsed by, authorized by, or operated on behalf of any federal, state, or local government entity.',
          'Ballotr does not provide official government services, voter registration, ballot submission, election administration, government benefits, licenses, permits, or legal advice.',
          'Users should verify government-related information through the original source links provided by Ballotr or directly through the relevant government entity.',
          'Ballotr is not a political committee, political action committee (PAC), campaign organization, or fundraising intermediary.',
          'Ballotr does not administer or control political fundraising conducted on third-party platforms.',
       ],
  },
  {
    title: '11. Sponsored Content',
    body: [
      'Some campaigns or content may be promoted through paid sponsorships.',
      'Sponsored content will be clearly labeled where applicable.',
      'Ballotr does not endorse the accuracy of sponsored content.',
    ],
  },
  {
    title: '12. Civic Insights and Aggregated Data',
    body: [
      'Ballotr may generate aggregated insights about civic engagement trends based on anonymized platform activity.',
      'These insights may be shared with organizations, researchers, journalists, or partners.',
      'Ballotr does not sell personal user data.',
      'Civic insights are produced only from anonymized and aggregated information and cannot identify individual users.',
    ],
  },
  {
    title: '13. Intellectual Property',
    body: [
      'The Ballotr platform, including its design, software, branding, and content, is the property of Bay View Concepts LLC or its licensors.',
      'Users may not reproduce or distribute platform content without permission.',
    ],
  },
  {
    title: '14. Termination and Account Deletion',
    body: [
      'Ballotr may suspend or terminate platform access for users who violate these Terms.',
      'Users may request deletion of their account and associated personal data by contacting Ballotr support at support@ballotr.com.',
      'Account deletion instructions are available at https://ballotr.com/delete-account.',
      'Ballotr may retain limited records where required for legal, security, fraud prevention, or compliance reasons.',
    ],
  },
  {
    title: '15. Disclaimer of Warranties',
    body: [
      'The Ballotr platform is provided “as-is” and “as-available.”',
      'Ballotr makes no guarantees regarding platform availability, reliability, or accuracy of content.',
    ],
  },
  {
    title: '16. Limitation of Liability',
    body: [
      'To the fullest extent permitted by law, Bay View Concepts LLC shall not be liable for indirect or consequential damages resulting from use of the platform.',
    ],
  },
  {
    title: '17. Governing Law',
    body: [
      'These Terms are governed by the laws of the United States and the State of Wisconsin.',
    ],
  },
  {
    title: '18. Contact Information',
    body: ['Questions regarding these Terms may be directed to:'],
    contact: ['Bay View Concepts LLC', 'legal@ballotr.com'],
  },
];

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_URL}/terms`} />
        <meta property="og:site_name" content="Ballotr" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}/terms`} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6 overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-gradient-to-r from-sky-50 to-white px-6 py-8 sm:px-8">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-sky-700">
                Ballotr Legal
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Terms of Service
              </h1>
              <p className="mt-3 text-sm text-slate-600">Effective Date: March 13, 2026</p>
            </div>

            <div className="px-6 py-8 sm:px-8">
              <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-base leading-7 text-slate-700">
                  These Terms of Service (“Terms”) govern your access to and use of the Ballotr
                  platform operated by <span className="font-semibold">Bay View Concepts LLC</span>{' '}
                  ("Ballotr," "we," "our," or "us").
                </p>
                <p className="mt-4 text-base leading-7 text-slate-700">
                  By accessing or using the Ballotr platform, you agree to these Terms. If you do
                  not agree, you may not use the platform.
                </p>
              </div>

              <div className="space-y-8">
                {sections.map((section) => (
                  <section key={section.title} className="scroll-mt-24">
                    <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>

                    {section.body?.map((paragraph) => (
                      <p key={paragraph} className="mt-3 text-base leading-7 text-slate-700">
                        {paragraph}
                      </p>
                    ))}

                    {section.bullets && (
                      <ul className="mt-4 space-y-2 pl-5 text-base leading-7 text-slate-700">
                        {section.bullets.map((bullet) => (
                          <li key={bullet} className="list-disc">
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.contact && (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-700">
                        {section.contact.map((line) => (
                          <p key={line} className="leading-7">
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;